import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { ArrowLeft, Search, UserX, Loader2 } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { toast } from 'sonner';
import { useUserData } from './UserDataContext';
import { ParallaxBackground } from './utils/parallax';
import { UserService } from '../services/api';
import { User as AppUser } from '../types';
import { useScreenReady } from '../contexts/ScreenTransitionContext';

interface BlockedUsersScreenProps {
  onBack: () => void;
}

export function BlockedUsersScreen({ onBack }: BlockedUsersScreenProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const { blockedUsers, unblockUser } = useUserData();
  const [hydratedUsers, setHydratedUsers] = useState<AppUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  useScreenReady(!isLoading);

  useEffect(() => {
    const hydrateUsers = async () => {
      if (!blockedUsers || blockedUsers.length === 0) {
        setHydratedUsers([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const users = await Promise.all(
          blockedUsers.map(async (id) => {
            try {
              return await UserService.fetchUserById(id);
            } catch (err) {
              console.error(`Failed to fetch blocked user ${id}:`, err);
              return null;
            }
          })
        );
        setHydratedUsers(users.filter((u): u is AppUser => u !== null));
      } catch (err) {
        console.error('Error hydrating blocked users:', err);
        toast.error('Failed to load some blocked users');
      } finally {
        setIsLoading(false);
      }
    };

    hydrateUsers();
  }, [blockedUsers]);

  const filteredBlockedUsers = hydratedUsers.filter((user) =>
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleUnblock = (userId: string | number, userName: string) => {
    unblockUser(String(userId));
    toast.success(`${userName} has been unblocked`, {
      description: 'You can now see their content and they can see yours',
      duration: 2000,
    });
  };

  return (
    <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] relative">
      <div className="absolute inset-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>
      <div className="w-full max-w-2xl mx-auto h-full flex flex-col relative z-10">
        {/* Header */}
        <div
          className="pb-4 px-4 border-b border-black/10 dark:border-white/10 bg-light-pattern dark:bg-[#121212]"
          style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))' }}
        >
          <div className="flex items-center gap-4 mb-4">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={onBack}
              className="text-black dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-[#4478ea]"
            >
              <ArrowLeft className="w-6 h-6" />
            </motion.button>
            <div>
              <h1 className="text-2xl text-black dark:text-white">
                Blocked Users
              </h1>
              <p className="text-sm text-[#666666] dark:text-[#a0a0a0]">
                {blockedUsers.length} {blockedUsers.length === 1 ? 'user' : 'users'} blocked
              </p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#999999]" />
            <Input
              placeholder="Search for blocked user..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#F5F5F5] dark:bg-[#1a1a1a] border-black/10 dark:border-white/10 text-black dark:text-white"
            />
          </div>
        </div>

        <ScrollArea className="flex-1" style={{ minHeight: 0 }}>
          <div className="p-4">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="w-10 h-10 text-[#F39C12] dark:text-[#4478ea] animate-spin mb-4" />
                <p className="text-[#666666] dark:text-[#a0a0a0]">Loading blocked users...</p>
              </div>
            ) : filteredBlockedUsers.length === 0 ? (
              <div className="text-center py-12">
                <UserX className="w-16 h-16 mx-auto mb-4 text-[#666666] dark:text-[#a0a0a0]" />
                <p className="text-[#666666] dark:text-[#a0a0a0] mb-2">
                  {searchQuery ? 'No blocked users found' : 'No blocked users'}
                </p>
                {!searchQuery && (
                  <p className="text-sm text-[#999999] dark:text-[#808080]">
                    Blocked users will appear here
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {filteredBlockedUsers.map((user) => (
                  <motion.div
                    key={user.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 rounded-2xl bg-[#F5F5F5] dark:bg-[#1a1a1a] border border-black/5 dark:border-white/10"
                  >
                    <div className="flex items-center gap-4">
                      <Avatar className="w-12 h-12">
                        <AvatarImage src={user.image || user.avatar} />
                        <AvatarFallback>{user.name[0]}</AvatarFallback>
                      </Avatar>

                      <div className="flex-1 min-w-0">
                        <p className="text-black dark:text-white truncate font-medium">{user.name}</p>
                        <p className="text-sm text-[#666666] dark:text-[#a0a0a0] truncate">
                          {user.username}
                        </p>
                      </div>

                      <Button
                        onClick={() => handleUnblock(user.id, user.name)}
                        variant="outline"
                        size="sm"
                        className="border-[#E74C3C] text-[#E74C3C] hover:bg-[#E74C3C] hover:text-white rounded-xl"
                      >
                        Unblock
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
}
